import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Overlay, OverlayConfig, OverlayModule, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { BehaviorSubject, filter, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { TemplatePortal } from '@angular/cdk/portal';

@Component({
  selector: 'bosch-ui-dropdown-mobile',
  standalone: true,
  imports: [CommonModule, OverlayModule, TranslateModule],
  templateUrl: './bosch-ui-dropdown-mobile.component.html',
  styleUrls: ['./bosch-ui-dropdown-mobile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoschUiDropdownMobileComponent implements OnInit, OnDestroy {
  @Input() set open(isOpen: boolean) {
    this._isOpen$.next(isOpen);
  }
  @Input() height = '50vh';
  @Input() hasBackdrop = true;
  @Input() backdropClass = 'tw-mobile-dropdown-backdrop';
  @Output() dropdownChange: EventEmitter<boolean> = new EventEmitter();
  @ViewChild('overlayContentTemplate', { static: true, read: TemplateRef })
  overlayContentTemplate!: TemplateRef<unknown>;

  private _isOpen$ = new BehaviorSubject(false);
  private _unsubscribe$: Subject<void> = new Subject<void>();
  private _overlayRef: OverlayRef | null = null;

  constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef) {}

  ngOnInit(): void {
    this.initOnOpenSubscription();
    this.initOnBackdropClick();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
  }

  onOpen(): void {
    this._isOpen$.next(!this._isOpen$.getValue());
  }

  private initOnOpenSubscription(): Subscription {
    return this._isOpen$.pipe(takeUntil(this._unsubscribe$)).subscribe((isOpen: boolean) => {
      if (isOpen && !this._overlayRef) {
        const OVERLAY_CONFIG = this.getOverlayConfig();
        this._overlayRef = this.overlay.create(OVERLAY_CONFIG);

        this._overlayRef.attach(new TemplatePortal(this.overlayContentTemplate, this.viewContainerRef));
      }

      if (!isOpen && this._overlayRef) {
        this._overlayRef.detach();
        this._overlayRef = null;
      }
    });
  }

  private initOnBackdropClick(): Subscription {
    return this._isOpen$
      .pipe(
        filter((isOpened: boolean) => isOpened),
        switchMap(() => {
          return this._overlayRef ? this._overlayRef.backdropClick() : of(null);
        }),
        takeUntil(this._unsubscribe$),
      )
      .subscribe(() => {
        this._isOpen$.next(false);
      });
  }

  private getOverlayConfig(): OverlayConfig {
    const positionStrategy: PositionStrategy = this.overlay.position().global().bottom().right();

    return new OverlayConfig({
      positionStrategy,
      width: '100%',
      height: this.height,
      hasBackdrop: this.hasBackdrop,
      backdropClass: [this.backdropClass],
    });
  }
}
